import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Tabs,
  Tab,
  Divider,
  Alert,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Slider,
  InputAdornment,
  IconButton,
  Chip,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import WorkflowExecutionSettings from '../WorkflowExecutionSettings';

const WorkflowSettingsPanel = ({ 
  workflow, 
  onUpdate, 
  onClose, 
  readOnly = false 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [localWorkflow, setLocalWorkflow] = useState({
    name: '',
    description: '',
    settings: {
      enabled: true,
      priority: 5,
      maxExecutionTime: 30000,
      concurrentExecutions: 1,
      rateLimiting: {
        enabled: true,
        maxExecutionsPerMinute: 60,
        maxExecutionsPerHour: 1000
      },
      scheduling: {
        enabled: false,
        timezone: 'UTC',
        cron: '',
        startDate: null,
        endDate: null
      },
      notifications: {
        onSuccess: { enabled: false, recipients: [] },
        onFailure: { enabled: true, recipients: [] },
        onChainComplete: { enabled: false, recipients: [] }
      }
    },
    executionControl: {
      mode: 'on_change',
      fieldTracking: { enabled: true, trackedFields: [], ignoreFields: [] },
      deduplication: { enabled: true, window: 5000, strategy: 'exact' },
      cascadePrevention: {
        enabled: true,
        maxDepth: 5,
        preventSelfTrigger: true,
        preventCircular: true,
        allowedSources: []
      },
      cooldown: { enabled: false, period: 10000, perEntity: true },
      executionWindow: { enabled: false },
      batching: { enabled: false },
      priority: { level: 5 },
      entityLocking: { enabled: false }
    },
    chainBehavior: {
      allowTriggeringOthers: true,
      canBeTriggeredByAutomation: true,
      maxChainDepth: 10,
      breakChainOnError: false,
      chainTimeout: 60000
    },
    safety: {
      loopPrevention: { enabled: true, maxIterations: 100, cooldownPeriod: 5000 },
      rollbackOnFailure: { enabled: false, snapshotData: true },
      circuitBreaker: {
        enabled: true,
        threshold: 5,
        timeout: 60000,
        resetTimeout: 300000
      },
      validation: {
        validateInputs: true,
        validateOutputs: false,
        strictMode: false
      }
    },
    metadata: {
      category: 'custom',
      tags: [],
      icon: '',
      color: ''
    },
    ...workflow
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (workflow) {
      setLocalWorkflow(prev => ({
        ...prev,
        ...workflow
      }));
    }
  }, [workflow]);

  const handleChange = (field, value) => {
    const newWorkflow = { ...localWorkflow };
    const fieldParts = field.split('.');
    let current = newWorkflow;
    
    for (let i = 0; i < fieldParts.length - 1; i++) {
      if (!current[fieldParts[i]]) {
        current[fieldParts[i]] = {};
      }
      current = current[fieldParts[i]];
    }
    
    current[fieldParts[fieldParts.length - 1]] = value;
    
    setLocalWorkflow(newWorkflow);
    setHasChanges(true);
  };

  const handleExecutionControlChange = (newSettings) => {
    setLocalWorkflow(prev => ({
      ...prev,
      executionControl: newSettings
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (validateSettings()) {
      onUpdate(localWorkflow);
      setHasChanges(false);
    }
  };

  const validateSettings = () => {
    const newErrors = {};
    
    if (!localWorkflow.name?.trim()) {
      newErrors.name = 'Workflow name is required';
    }
    
    if (localWorkflow.settings.maxExecutionTime < 1000) {
      newErrors.maxExecutionTime = 'Execution time must be at least 1 second';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const TabPanel = ({ children, value, index }) => (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ p: 2, height: 'calc(100% - 48px)', overflowY: 'auto' }}
    >
      {value === index && children}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography variant="h6">Workflow Settings</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(e, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="General" />
        <Tab label="Execution Control" />
        <Tab label="Performance" />
        <Tab label="Safety" />
        <Tab label="Notifications" />
      </Tabs>

      {/* General Tab */}
      <TabPanel value={activeTab} index={0}>
        <TextField
          fullWidth
          label="Workflow Name"
          value={localWorkflow.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          value={localWorkflow.description}
          onChange={(e) => handleChange('description', e.target.value)}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.settings.enabled}
              onChange={(e) => handleChange('settings.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Workflow Enabled"
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Priority Level: {localWorkflow.settings.priority}
        </Typography>
        <Slider
          value={localWorkflow.settings.priority}
          onChange={(e, value) => handleChange('settings.priority', value)}
          min={1}
          max={10}
          marks
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel>Category</FormLabel>
          <Select
            value={localWorkflow.metadata?.category || 'custom'}
            onChange={(e) => handleChange('metadata.category', e.target.value)}
            disabled={readOnly}
          >
            <MenuItem value="task_management">Task Management</MenuItem>
            <MenuItem value="content_workflow">Content Workflow</MenuItem>
            <MenuItem value="team_coordination">Team Coordination</MenuItem>
            <MenuItem value="communication">Communication</MenuItem>
            <MenuItem value="reporting">Reporting</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>
      </TabPanel>

      {/* Execution Control Tab */}
      <TabPanel value={activeTab} index={1}>
        <WorkflowExecutionSettings
          executionControl={localWorkflow.executionControl}
          onChange={handleExecutionControlChange}
          errors={errors.executionControl}
          disabled={readOnly}
          showAdvanced={true}
        />
      </TabPanel>

      {/* Performance Tab */}
      <TabPanel value={activeTab} index={2}>
        <TextField
          fullWidth
          type="number"
          label="Max Execution Time"
          value={localWorkflow.settings.maxExecutionTime}
          onChange={(e) => handleChange('settings.maxExecutionTime', parseInt(e.target.value))}
          InputProps={{
            endAdornment: <InputAdornment position="end">ms</InputAdornment>
          }}
          error={!!errors.maxExecutionTime}
          helperText={errors.maxExecutionTime || 'Maximum time allowed for workflow execution'}
          disabled={readOnly}
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          type="number"
          label="Concurrent Executions"
          value={localWorkflow.settings.concurrentExecutions}
          onChange={(e) => handleChange('settings.concurrentExecutions', parseInt(e.target.value))}
          helperText="Number of parallel executions allowed"
          disabled={readOnly}
          sx={{ mb: 2 }}
          inputProps={{ min: 1, max: 10 }}
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Rate Limiting
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.settings.rateLimiting?.enabled}
              onChange={(e) => handleChange('settings.rateLimiting.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Enable Rate Limiting"
        />
        
        {localWorkflow.settings.rateLimiting?.enabled && (
          <>
            <TextField
              fullWidth
              type="number"
              label="Max Executions Per Minute"
              value={localWorkflow.settings.rateLimiting.maxExecutionsPerMinute}
              onChange={(e) => handleChange('settings.rateLimiting.maxExecutionsPerMinute', parseInt(e.target.value))}
              disabled={readOnly}
              sx={{ mb: 2, mt: 2 }}
            />
            
            <TextField
              fullWidth
              type="number"
              label="Max Executions Per Hour"
              value={localWorkflow.settings.rateLimiting.maxExecutionsPerHour}
              onChange={(e) => handleChange('settings.rateLimiting.maxExecutionsPerHour', parseInt(e.target.value))}
              disabled={readOnly}
            />
          </>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Chain Behavior
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.chainBehavior?.allowTriggeringOthers}
              onChange={(e) => handleChange('chainBehavior.allowTriggeringOthers', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Can Trigger Other Workflows"
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.chainBehavior?.canBeTriggeredByAutomation}
              onChange={(e) => handleChange('chainBehavior.canBeTriggeredByAutomation', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Can Be Triggered by Automation"
        />
        
        <TextField
          fullWidth
          type="number"
          label="Max Chain Depth"
          value={localWorkflow.chainBehavior?.maxChainDepth || 10}
          onChange={(e) => handleChange('chainBehavior.maxChainDepth', parseInt(e.target.value))}
          disabled={readOnly}
          sx={{ mt: 2 }}
          inputProps={{ min: 1, max: 50 }}
        />
      </TabPanel>

      {/* Safety Tab */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="subtitle1" gutterBottom>
          Loop Prevention
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.safety?.loopPrevention?.enabled}
              onChange={(e) => handleChange('safety.loopPrevention.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Enable Loop Prevention"
        />
        
        {localWorkflow.safety?.loopPrevention?.enabled && (
          <TextField
            fullWidth
            type="number"
            label="Max Iterations"
            value={localWorkflow.safety?.loopPrevention?.maxIterations || 100}
            onChange={(e) => handleChange('safety.loopPrevention.maxIterations', parseInt(e.target.value))}
            disabled={readOnly}
            sx={{ mt: 2 }}
          />
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Circuit Breaker
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.safety?.circuitBreaker?.enabled}
              onChange={(e) => handleChange('safety.circuitBreaker.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Enable Circuit Breaker"
        />
        
        {localWorkflow.safety?.circuitBreaker?.enabled && (
          <>
            <TextField
              fullWidth
              type="number"
              label="Failure Threshold"
              value={localWorkflow.safety?.circuitBreaker?.threshold || 5}
              onChange={(e) => handleChange('safety.circuitBreaker.threshold', parseInt(e.target.value))}
              helperText="Number of failures before circuit opens"
              disabled={readOnly}
              sx={{ mt: 2 }}
            />
            
            <TextField
              fullWidth
              type="number"
              label="Reset Timeout"
              value={localWorkflow.safety?.circuitBreaker?.resetTimeout || 300000}
              onChange={(e) => handleChange('safety.circuitBreaker.resetTimeout', parseInt(e.target.value))}
              InputProps={{
                endAdornment: <InputAdornment position="end">ms</InputAdornment>
              }}
              helperText="Time before attempting to close circuit"
              disabled={readOnly}
              sx={{ mt: 2 }}
            />
          </>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Rollback
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.safety?.rollbackOnFailure?.enabled}
              onChange={(e) => handleChange('safety.rollbackOnFailure.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Enable Rollback on Failure"
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Validation
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.safety?.validation?.validateInputs}
              onChange={(e) => handleChange('safety.validation.validateInputs', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Validate Inputs"
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.safety?.validation?.strictMode}
              onChange={(e) => handleChange('safety.validation.strictMode', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Strict Mode"
        />
      </TabPanel>

      {/* Notifications Tab */}
      <TabPanel value={activeTab} index={4}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure who should be notified about workflow execution events
        </Alert>
        
        <Typography variant="subtitle1" gutterBottom>
          On Success
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.settings.notifications?.onSuccess?.enabled}
              onChange={(e) => handleChange('settings.notifications.onSuccess.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Notify on successful execution"
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          On Failure
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.settings.notifications?.onFailure?.enabled}
              onChange={(e) => handleChange('settings.notifications.onFailure.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Notify on failed execution"
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          On Chain Complete
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={localWorkflow.settings.notifications?.onChainComplete?.enabled}
              onChange={(e) => handleChange('settings.notifications.onChainComplete.enabled', e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Notify when workflow chain completes"
        />
      </TabPanel>

      {/* Footer */}
      <Box sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {hasChanges && (
          <Alert severity="info" sx={{ py: 0 }}>
            You have unsaved changes
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={readOnly || !hasChanges}
          >
            Save Settings
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkflowSettingsPanel;